<template>
  <div :class="[status]">
    <!-- Error -->
    <slot v-if="status == 'pending'" name="loading">
      <NuxtLoadingIndicator />
    </slot>

    <!-- Error -->
    <slot v-if="status == 'error'" name="error" :error="error">
      <div class="error">{{ error }}</div>
    </slot>

    <!-- Items -->
    <template v-if="data && data.data && data.data.length > 0">
      <slot name="items" :data="data">
        <component
          :is="itemsWrapper"
          :id="`items-${collection}`"
          class="items"
          :class="itemsClass"
        >
          <slot name="customItem" />

          <component
            :is="itemWrapper"
            v-for="(item, index) in data.data"
            :key="`${collection}-${index}-${item.id}-${filtersId}`"
          >
            <Component :is="itemComponent" :item="item" :idx="index" />
          </component>
        </component>
      </slot>
    </template>
    <slot v-else-if="status == 'success'" name="empty">
      <div :id="`items-${collection}`" class="items is-empty">
        {{ $t(`${collection}.results.empty`) }}
      </div>
    </slot>

    <!-- Pagination -->
    <slot v-if="usePagination" name="pagination">
      <div :id="`pagination-${collection}`" class="pagination">
        <Component :is="paginationComponent" :pagination-id="filtersId" />
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type {
  StrapiLocale,
  MetaResponsePaginationByPage,
} from "@nuxtjs/strapi";
import type { StrapiAttributes } from "../../../../../src/types";
import {
  useStrapi,
  useI18n,
  useCollectionConfig,
  useFilters,
  useState,
  toRefs,
  unref,
  provide,
  useAsyncData,
  watch,
  useRoute,
  useNuxtApp,
  onUnmounted,
} from "#imports";
import { useInjectionKeys } from "../../../composables/useInjectionKeys";
import { deepmerge } from "deepmerge-ts";
import type { ConcreteComponent } from "vue";

const { find } = useStrapi();
const { locale } = useI18n();
const { getCollectionConfig } = useCollectionConfig();
const route = useRoute();

// Component properties
export interface StrapiCollectionProps {
  filtersId: string;
  itemsClass?: string[];
  collection: string;
  usePagination?: boolean;
  populate?: Record<string, unknown>;
  staticFilters?: Record<string, unknown>;
  sort?: string | string[];
  itemsWrapper: ConcreteComponent | string;
  itemWrapper: ConcreteComponent | string;
  stateKey?: string;
}

const props = withDefaults(defineProps<StrapiCollectionProps>(), {
  itemsClass: () => [],
  usePagination: true,
  populate: () => ({}),
  staticFilters: () => ({}),
  sort: undefined,
  stateKey: undefined,
});
const currentLocale: StrapiLocale = unref(locale) as StrapiLocale;
const { itemComponent } = getCollectionConfig(props.collection);
//create refs from props
const {
  collection,
  populate: propsPopulate,
  sort,
  staticFilters,
} = toRefs(props);

//get collection config
const {
  plural,
  populate,
  paginationComponent,
  sort: defaultSort,
} = getCollectionConfig(props.collection);

const { filters, pagination } = useFilters(`${props.filtersId}`);

//when the filter-bar-filters change, we want to update this in the params. When the params change, the data is fetched.
const params = useState(
  `${props.stateKey ? `${props.stateKey}/` : ""}collection/${collection.value}/${route.path}`,
  () => ({
    locale: currentLocale,
    populate: deepmerge(propsPopulate.value, populate),
    pagination: {
      page: pagination.value.page,
      pageSize: pagination.value.pageSize,
    },
    filters: deepmerge(staticFilters.value, filters.value),
    sort: sort.value ?? defaultSort,
  }),
);
const staticFiltersStopHandle = watch(
  () => props.staticFilters,
  () => {
    const p = params.value;

    const newFilters = deepmerge(staticFilters.value, filters.value);
    if (JSON.stringify(p.filters) === JSON.stringify(newFilters)) return;
    p.filters = deepmerge(staticFilters.value, filters.value);
    params.value = { ...p };
  },
);
const filtersStopHandle = watch(filters, () => {
  const p = params.value;

  const newFilters = deepmerge(staticFilters.value, filters.value);
  if (JSON.stringify(p.filters) === JSON.stringify(newFilters)) return;
  p.filters = deepmerge(staticFilters.value, filters.value);
  params.value = { ...p };
});
const paginationStopHandle = watch(pagination, () => {
  if (!pagination.value) return;
  const p = params.value;
  if (
    p.pagination.page == pagination.value.page &&
    p.pagination.pageSize == pagination.value.pageSize
  ) {
    return;
  }
  p.pagination = {
    page: pagination.value.page,
    pageSize: pagination.value.pageSize,
  };
  params.value = { ...p };
});
const stateKeyStopHandle = watch(
  () => props.stateKey,
  () => {
    params.value = {
      ...params.value,
    };
  },
);

const nuxtApp = useNuxtApp();
const { data, status, error } = await useAsyncData(
  `${props.stateKey ? `${props.stateKey}/` : ""}${locale.value}/collection/${collection.value}/${route.path}`,
  () => find<StrapiAttributes>(`${plural}`, params.value),
  {
    watch: [params],
    getCachedData: (key) =>
      nuxtApp.$config.public.flyingkiwi.enableCache
        ? nuxtApp.payload.data[key] || nuxtApp.static.data[key]
        : nuxtApp.isHydrating
          ? nuxtApp.payload.data[key]
          : nuxtApp.static.data[key],
  },
);

const dataStopHandle = watch(
  data,
  () => {
    if (
      JSON.stringify(pagination.value) !==
      JSON.stringify(data.value?.meta.pagination)
    ) {
      pagination.value = data.value?.meta
        .pagination as MetaResponsePaginationByPage;
    }
  },
  {
    immediate: true,
  },
);

const { collectionKey, itemsKey } = useInjectionKeys();

provide(collectionKey, props.collection);
provide(itemsKey, data.value?.data ?? []);
provide("status", status.value);

onUnmounted(() => {
  staticFiltersStopHandle();
  filtersStopHandle();
  paginationStopHandle();
  stateKeyStopHandle();
  dataStopHandle();
});
</script>
